
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { UserProfile, UserInvite } from '../../types';
import { Button } from '../Button';
import { Search, UserPlus, UserCheck, Loader2, Send, X, Mail, Clock, GraduationCap, RotateCcw, Ban, LockOpen } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { sameCompany } from '../../services/companyScope';
import { setzeErsteinweisungZurueck } from '../../services/tutorial/tutorialService';
import { ladeSperrstatus, setzeSperre, istGesperrt, type SperrStatus } from '../../services/benutzerSperre';
import { aktualisiereGesperrteKonten } from '../../services/gesperrteKonten';
import { fetchSifaFachkraefte, setzeSifaFachkraft } from '../../services/sifa/vorlagenService';
import { ladeRollenStand, setzeBenutzerRolle, type RollenStand } from '../../services/rechte/rollenVerwaltung';
import { aktualisiereMeineRechte } from '../../services/rechte/rechteService';
import { fehlerText, firmenZumLaden, personName, rollenSpalte, staendeMit, standMitBenutzerRolle, standNichtVerfuegbar } from './rollenRechteLogic';
import { BenutzerRollenChips } from './BenutzerRollenChips';

/** Kopie der Menge mit bzw. ohne `id` — State nie an Ort und Stelle ändern. */
const mitOderOhne = (s: ReadonlySet<string>, id: string, drin: boolean): Set<string> => {
    const neu = new Set(s);
    if (drin) neu.add(id); else neu.delete(id);
    return neu;
};

const SIFA_141_FEHLT = 'Datenbank-Erweiterung Nr. 141 fehlt.';

/** Fehlt die Tabelle (Nr. 141), sagt der Service das im Text — hier kurz. */
const sifaFehlerText = (e: unknown): string => {
    const msg = (e as { message?: string } | null)?.message || String(e);
    return /Nr\. 141\b/.test(msg) ? SIFA_141_FEHLT : msg;
};

/**
 * Systemrollen, die eine Einladung vergeben kann — Schlüssel = `profiles.role`.
 * „Arbeitsvorbereitung" nicht mehr: neue Leute kommen als Mitarbeiter und
 * bekommen ihre fachlichen Rollen in der Spalte „Rollen" (Rollensystem §3.1/§7).
 */
const ROLLEN_EINLADUNG: Record<string, string> = {
    user: 'Mitarbeiter',
    freelancer: 'Freelancer',
    admin: 'Admin',
};

/**
 * Beschriftung der Systemrolle in der Tabelle. `arbeitsvorbereitung` ist ein
 * Altwert und bleibt wählbar, bis Stufe 2 ihn auf Mitarbeiter + Rolle
 * „Arbeitsvorbereitung" umstellt (Plan §7). `super_admin` bietet die Tabelle
 * nur einem Super-Admin an.
 */
const SYSTEMROLLEN: Record<string, string> = {
    user: 'Mitarbeiter',
    arbeitsvorbereitung: 'Arbeitsvorbereitung (alt)',
    freelancer: 'Freelancer',
    admin: 'Admin',
    super_admin: 'Super Admin',
};

interface Props {
  users: UserProfile[];
  invites: UserInvite[];
  currentUser: UserProfile | null;
  onRefresh: () => void;
}

export const AdminUserManagement: React.FC<Props> = ({ users, invites, currentUser, onRefresh }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [inviteModal, setInviteModal] = useState(false);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [newUserData, setNewUserData] = useState({ email: '', first_name: '', last_name: '', role: 'user', company: '' });
    const [statusFeedback, setStatusFeedback] = useState<{ type: 'success' | 'error' | 'warning', message: string } | null>(null);
    /** Nutzer, dessen Ersteinweisung gerade zurückgesetzt wird — sperrt nur dessen Knopf. */
    const [resetLaeuft, setReset] = useState<string | null>(null);
    /** Nutzer, dessen Freischaltung gerade geschrieben wird. */
    const [approvalLaeuft, setApprovalLaeuft] = useState<string | null>(null);
    /**
     * Sperrstatus je Konto (Migration Nr. 138). `null` = (noch) unbekannt —
     * dann sind die Sperr-Knöpfe aus, und `sperreHinweis` sagt warum (z. B.
     * Migration offen). `auth.users` ist nur über die RPC lesbar.
     */
    const [sperrStatus, setSperrStatus] = useState<Map<string, SperrStatus> | null>(null);
    const [sperreHinweis, setSperreHinweis] = useState<string | null>(null);
    const [sperreLaeuft, setSperreLaeuft] = useState<string | null>(null);

    const ladeSperren = useCallback(async () => {
        const r = await ladeSperrstatus();
        if (r.verfuegbar) { setSperrStatus(r.status); setSperreHinweis(null); }
        else { setSperrStatus(null); setSperreHinweis(r.grund); }
    }, []);
    useEffect(() => { void ladeSperren(); }, [ladeSperren, users]);

    /**
     * Häkchen „SiFa" je Konto (Nr. 141, docs/sifa-vorlagen-plan.md §3h).
     * `null` = nicht lesbar — dann ist die Spalte aus und `sifaHinweis` sagt
     * warum. Eine leere Menge als Ersatz sähe aus wie „niemand hat es".
     */
    const [sifaSet, setSifaSet] = useState<Set<string> | null>(null);
    const [sifaHinweis, setSifaHinweis] = useState<string | null>(null);
    const [sifaLaeuft, setSifaLaeuft] = useState<ReadonlySet<string>>(() => new Set());
    // Ref neben dem State: sperrt den zweiten Klick, bevor `disabled` gerendert ist.
    const sifaLaeuftRef = useRef<Set<string>>(new Set());
    // Nur die jüngste Ladeantwort zählt — `users` wechselt beim Nachladen schnell hintereinander.
    const sifaLadeNr = useRef(0);
    // Ein Schreibversuch hat „Nr. 141 fehlt" gemeldet — das Nachladen läse
    // wieder eine leere Menge und schaltete die Spalte zurück an.
    const sifaOhneTabelle = useRef(false);

    const ladeSifa = useCallback(async () => {
        if (sifaOhneTabelle.current) return;
        const nr = ++sifaLadeNr.current;
        try {
            const s = await fetchSifaFachkraefte();
            if (nr !== sifaLadeNr.current) return;
            setSifaSet(s); setSifaHinweis(null);
        } catch (e) {
            if (nr !== sifaLadeNr.current) return;
            setSifaSet(null); setSifaHinweis(sifaFehlerText(e));
        }
    }, []);
    useEffect(() => { void ladeSifa(); }, [ladeSifa, users]);

    const setzeSifaLaeuft = (id: string, laeuft: boolean) => {
        if (laeuft) sifaLaeuftRef.current.add(id); else sifaLaeuftRef.current.delete(id);
        setSifaLaeuft(new Set(sifaLaeuftRef.current));
    };

    /**
     * Häkchen umschalten — optimistisch, bei Fehler zurück. Freelancer
     * bekommen es nie (RESTRICTIVE-Gate in Nr. 141); ein Altbestand lässt
     * sich nur noch entfernen.
     */
    const umschaltenSifa = async (u: UserProfile, an: boolean) => {
        if (!sifaSet || sifaLaeuftRef.current.has(u.id)) return;
        if (an && u.role === 'freelancer') return;
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
        setzeSifaLaeuft(u.id, true);
        setStatusFeedback(null);
        // Eine Ladeantwort von vor dem Schreiben überschriebe sonst den optimistischen Stand.
        sifaLadeNr.current++;
        setSifaSet(prev => prev && mitOderOhne(prev, u.id, an));
        try {
            await setzeSifaFachkraft(u.id, an);
            setStatusFeedback({ type: 'success', message: an ? `${name} hat jetzt das Häkchen „SiFa".` : `${name}: Häkchen „SiFa" entfernt.` });
        } catch (e) {
            const text = sifaFehlerText(e);
            setSifaSet(prev => prev && mitOderOhne(prev, u.id, !an));
            setStatusFeedback({ type: 'error', message: `Häkchen „SiFa" nicht gespeichert: ${text}` });
            // Ohne Tabelle liest der Service eine leere Menge (kein Fehler) —
            // erst der Schreibversuch verrät es. Dann die Spalte abschalten,
            // statt jeden weiteren Klick scheitern zu lassen.
            if (text === SIFA_141_FEHLT) {
                sifaOhneTabelle.current = true;
                setSifaSet(null); setSifaHinweis(text);
            }
        } finally {
            // Auch eine während des Schreibens gestartete Ladung kann den alten Stand gelesen haben.
            sifaLadeNr.current++;
            setzeSifaLaeuft(u.id, false);
        }
    };

    const sifaHaekchen = (u: UserProfile) => {
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
        const an = !!sifaSet?.has(u.id);
        const laeuft = sifaLaeuft.has(u.id);
        const freelancer = u.role === 'freelancer';
        const aus = !sifaSet || laeuft || (freelancer && !an);
        const titel = !sifaSet ? (sifaHinweis || 'Häkchen „SiFa" wird geladen …')
            : freelancer && !an ? 'Freelancer bekommen das Häkchen „SiFa" nie.'
            : freelancer ? 'Freelancer bekommen das Häkchen „SiFa" nie — entfernen.'
            : an ? 'Häkchen „SiFa" entfernen — Unterweisungs-Vorlagen dann nur noch lesen.'
            : 'Häkchen „SiFa" setzen — darf Unterweisungs-Vorlagen anlegen und bearbeiten.';
        return (
            <label className={`inline-flex items-center gap-1.5 ${aus ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}`} title={titel}>
                <input
                    type="checkbox"
                    checked={an}
                    disabled={aus}
                    onChange={() => { void umschaltenSifa(u, !an); }}
                    aria-label={`Häkchen „SiFa" für ${name}`}
                    className="w-4 h-4 accent-indigo-600 disabled:cursor-not-allowed"
                />
                {laeuft && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400"/>}
            </label>
        );
    };

    /**
     * Rollen je Person (Rollensystem, docs/rollensystem-plan.md §6.1): der Stand
     * jeder Firma, deren Leute hier stehen — je Firma einmal geladen, für einen
     * Admin nur die eigene. Fehlt eine Firma in der Map, lädt sie noch; ist sie
     * nicht verfügbar (Migrationen Nr. 149/150 offen), zeigt die Spalte einen
     * Strich mit dem Grund statt Knöpfen.
     */
    const [rollenStaende, setRollenStaende] = useState<ReadonlyMap<string, RollenStand>>(() => new Map());
    const [rollenLaufen, setRollenLaufen] = useState<ReadonlySet<string>>(() => new Set());
    // Ref neben dem State: sperrt den zweiten Klick, bevor `disabled` gerendert ist.
    const rollenLaufenRef = useRef<Set<string>>(new Set());
    // Nur die jüngste Ladeantwort zählt — und keine, die vor einem Schreiben losging.
    const rollenLadeNr = useRef(0);
    // Während geschrieben wird, gilt keine Ladeantwort — sie kann den Stand von
    // VOR dem Schreiben tragen und den optimistischen Stand überdecken. Nach dem
    // letzten Schreiben wird deshalb immer einmal neu geladen, mit dem dann
    // aktuellen Firmenschlüssel (Ref, nicht der Wert aus dem Klick).
    const rollenFirmenRef = useRef('');
    const rollenFirmen = useMemo(() => firmenZumLaden(users, currentUser), [users, currentUser]);
    // Als Text in die Abhängigkeiten: `users` kommt bei jedem Nachladen als neues Feld.
    const rollenFirmenSchluessel = rollenFirmen.join('|');
    rollenFirmenRef.current = rollenFirmenSchluessel;

    const ladeRollen = useCallback(async (firmenSchluessel: string) => {
        // Läuft ein Schreiben, lädt dessen Abschluss ohnehin neu.
        if (rollenLaufenRef.current.size > 0) return;
        const nr = ++rollenLadeNr.current;
        const firmen = firmenSchluessel ? firmenSchluessel.split('|') : [];
        const staende = await Promise.all(firmen.map(async (id): Promise<[string, RollenStand]> => {
            try {
                return [id, await ladeRollenStand(id)];
            } catch (e) {
                return [id, standNichtVerfuegbar(id, fehlerText(e))];
            }
        }));
        if (nr !== rollenLadeNr.current || rollenLaufenRef.current.size > 0) return;
        setRollenStaende(new Map(staende));
    }, []);
    useEffect(() => { void ladeRollen(rollenFirmenSchluessel); }, [ladeRollen, rollenFirmenSchluessel, users]);

    /** Grund für den Spaltenkopf, solange die Rollen irgendwo nicht lesbar sind. */
    const rollenHinweis = useMemo(() => {
        for (const s of rollenStaende.values()) if (!s.verfuegbar) return s.grund;
        return null;
    }, [rollenStaende]);

    const setzeRollenLaeuft = (key: string, laeuft: boolean) => {
        if (laeuft) rollenLaufenRef.current.add(key); else rollenLaufenRef.current.delete(key);
        setRollenLaufen(new Set(rollenLaufenRef.current));
    };

    /** Rolle geben/nehmen — optimistisch, bei Fehler zurück (Muster SiFa-Häkchen). */
    const setzeRolle = async (u: UserProfile, companyId: string, rolle: string, an: boolean) => {
        const stand = rollenStaende.get(companyId);
        if (!stand?.verfuegbar) return;
        // Admins haben alles, Freelancer bekommen nie Rollen (Plan §3.1).
        if (an && (u.role === 'freelancer' || u.role === 'admin' || u.role === 'super_admin')) return;
        const key = `${u.id}|${rolle}`;
        if (rollenLaufenRef.current.has(key)) return;
        const name = personName(u);
        const rollenName = stand.rollen.find(r => r.schluessel === rolle)?.name || rolle;
        const setze = (wert: boolean) =>
            setRollenStaende(prev => staendeMit(prev, companyId, s => standMitBenutzerRolle(s, u.id, rolle, wert)));
        setzeRollenLaeuft(key, true);
        setStatusFeedback(null);
        // Eine Ladeantwort von vor dem Schreiben überschriebe sonst den optimistischen Stand.
        rollenLadeNr.current++;
        setze(an);
        try {
            const r = await setzeBenutzerRolle(u.id, rolle, an);
            if (!r.ok) throw new Error(r.meldung || 'unbekannter Fehler');
            setStatusFeedback({ type: 'success', message: an ? `${name} hat jetzt die Rolle „${rollenName}".` : `${name}: Rolle „${rollenName}" entfernt.` });
            // Betrifft die Änderung die eigene Person, ändern sich ihre Rechte sofort.
            aktualisiereMeineRechte().catch(e => console.warn('Eigene Rechte nicht nachgeladen:', e));
        } catch (e) {
            setze(!an);
            setStatusFeedback({ type: 'error', message: `Rolle „${rollenName}" nicht ${an ? 'vergeben' : 'entfernt'}: ${fehlerText(e)}` });
        } finally {
            // Auch eine während des Schreibens gestartete Ladung kann den alten Stand gelesen haben.
            rollenLadeNr.current++;
            setzeRollenLaeuft(key, false);
            if (rollenLaufenRef.current.size === 0) void ladeRollen(rollenFirmenRef.current);
        }
    };

    const rollenZelle = (u: UserProfile) => {
        const spalte = rollenSpalte(u, rollenStaende, rollenFirmen);
        const laeuft = new Set<string>();
        for (const key of rollenLaufen) {
            if (key.startsWith(`${u.id}|`)) laeuft.add(key.slice(u.id.length + 1));
        }
        return (
            <BenutzerRollenChips
                name={personName(u)}
                spalte={spalte}
                laeuft={laeuft}
                onSetzen={(rolle, an) => { if (spalte.art === 'chips') void setzeRolle(u, spalte.companyId, rolle, an); }}
            />
        );
    };

    const filteredUsers = useMemo(() => {
        if (!currentUser) return [];
        let list = users;
        if (currentUser.role !== 'super_admin') {
            list = list.filter(u => sameCompany(u, currentUser));
        }
        const search = searchTerm.toLowerCase();
        // Wartende zuerst — ein neuer Mitarbeiter soll nicht irgendwo im
        // Alphabet stehen, während er auf die Freischaltung wartet. Die
        // Sortierung ist stabil, innerhalb der Gruppen bleibt es beim Nachnamen.
        return list.filter(u =>
            (u.first_name || '').toLowerCase().includes(search) ||
            (u.last_name || '').toLowerCase().includes(search) ||
            u.email.toLowerCase().includes(search) ||
            (u.company_name || '').toLowerCase().includes(search)
        ).sort((a, b) => Number(!!a.is_approved) - Number(!!b.is_approved));
    }, [users, currentUser, searchTerm]);

    const pendingCount = filteredUsers.filter(u => !u.is_approved).length;

    const filteredInvites = useMemo(() => {
        if (!invites) return [];
        return invites.filter(i => i.email.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [invites, searchTerm]);

    // Einladung für eine Adresse, zu der es schon ein Profil gibt. Seit Nr. 124
    // verbraucht handle_new_user jede Einladung, die Migration räumt auch den
    // Bestand ab — bleibt trotzdem eine stehen (App vor der Migration, erneute
    // Einladung), wird sie MARKIERT statt versteckt: sie ist eine Eintrittskarte
    // für eine Neuregistrierung und muss widerrufbar bleiben.
    const registrierteEmails = useMemo(
        () => new Set(users.map(u => (u.email || '').trim().toLowerCase())),
        [users],
    );

    const updateUserRole = async (id: string, newRole: string) => {
        if (!confirm(`Systemrolle wirklich auf "${SYSTEMROLLEN[newRole] || newRole}" ändern?`)) return;
        const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', id);
        if (!error) onRefresh(); else alert("Fehler beim Ändern der Rolle");
    };
  
    /**
     * Freischalten.
     *
     * Früher war die gelbe „Wartet"-Pille selbst der Knopf — das sah niemand
     * (Tailwind v4 gibt Buttons keinen Hand-Cursor, die Pille hat keinen
     * Hover), und ein Fehler verschwand ohne Meldung. `.select('id')` macht
     * zusätzlich den stillen RLS-Fall sichtbar: ein Update, das die Policy
     * wegfiltert, liefert keinen Fehler, sondern null Zeilen.
     *
     * Ein „Sperren" gibt es bewusst nicht: `is_approved` wird weder beim
     * Anmelden noch in RLS durchgesetzt, der Knopf hätte eine Sperre gemeldet,
     * die nicht stattfindet (Codex-Review 25.09.). Seit Nr. 124 schaltet die
     * Einladung frei; hier landen nur noch Altfälle.
     */
    const approveUser = async (u: UserProfile) => {
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
        setApprovalLaeuft(u.id);
        setStatusFeedback(null);
        try {
            const { data, error } = await supabase.from('profiles').update({ is_approved: true }).eq('id', u.id).select('id');
            if (error) throw error;
            if (!data || data.length === 0) {
                throw new Error('Die Datenbank hat keine Zeile geändert — vermutlich fehlt die Berechtigung (RLS auf profiles).');
            }
            setStatusFeedback({ type: 'success', message: `${name} ist freigeschaltet.` });
            onRefresh();
        } catch (e) {
            const msg = (e as { message?: string } | null)?.message || String(e);
            setStatusFeedback({ type: 'error', message: `Freischalten fehlgeschlagen: ${msg}` });
        } finally { setApprovalLaeuft(null); }
    };
  
    /**
     * Ersteinweisung zurücksetzen — der Nutzer bekommt die Willkommens-Tour
     * beim nächsten Anmelden erneut (`profiles.ersteinweisung_am = null`,
     * Migration Nr. 102).
     *
     * Der Merker im Browser des Nutzers (`lernzentrum.willkommen`) bleibt
     * davon unberührt und liegt ausserhalb unserer Reichweite — auf SEINEM
     * bisherigen Gerät bleibt die Tour deshalb aus, auf jedem anderen und nach
     * einem geleerten Speicher läuft sie. Das steht als Hinweis im Dialog,
     * damit niemand den Knopf für kaputt hält.
     */
    const resetErsteinweisung = async (u: UserProfile) => {
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
        if (!confirm(
            `Ersteinweisung für ${name} zurücksetzen?\n\n`
            + 'Die Willkommens-Tour läuft dann beim nächsten Anmelden erneut.\n'
            + 'Hinweis: auf dem Gerät, auf dem die Tour zuletzt lief, merkt sich der Browser '
            + 'den Durchlauf zusätzlich — dort startet sie erst nach dem Leeren der '
            + 'Website-Daten wieder von selbst. Im Lernzentrum lässt sie sich jederzeit von '
            + 'Hand starten.',
        )) return;
        setReset(u.id);
        try {
            await setzeErsteinweisungZurueck(u.id);
            onRefresh();
        } catch (e: any) {
            // 42703 = Spalte fehlt: Migration Nr. 102 ist noch nicht gefahren.
            alert(e?.code === '42703'
                ? 'Die Spalte „ersteinweisung_am" gibt es in dieser Datenbank noch nicht (Migration Nr. 102 offen).'
                : 'Zurücksetzen fehlgeschlagen: ' + (e?.message || String(e)));
        } finally { setReset(null); }
    };

    /**
     * Sperren statt löschen (Migration Nr. 138). Früher entfernte dieser Knopf
     * nur die `profiles`-Zeile: das Anmeldekonto blieb, und ein Konto ohne
     * Profil gilt in der Datenbank als intern. Die Sperre weist Anmeldung und
     * Token-Erneuerung ab und beendet alle Sitzungen; Profil und Nachweise
     * („geprüft von", „hochgeladen von" …) bleiben, entsperren geht jederzeit.
     */
    const handleSperre = async (u: UserProfile, sperren: boolean) => {
        const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
        if (!confirm(sperren
            ? `${name} sperren?\n\nDie Anmeldung wird ab sofort abgewiesen, alle Sitzungen werden beendet. `
              + 'Ein schon geöffneter Tab kann höchstens noch bis zu einer Stunde weiterarbeiten. '
              + 'Profil, Berichte und Nachweise bleiben erhalten; entsperren ist jederzeit möglich.'
            : `${name} entsperren?\n\nDie Person kann sich danach wieder anmelden.`)) return;
        setSperreLaeuft(u.id);
        setStatusFeedback(null);
        try {
            const r = await setzeSperre(u.id, sperren);
            if (!r.ok) {
                setStatusFeedback({ type: 'error', message: `${sperren ? 'Sperren' : 'Entsperren'} fehlgeschlagen: ${r.meldung}` });
                return;
            }
            setStatusFeedback({ type: 'success', message: sperren ? `${name} ist gesperrt.` : `${name} ist entsperrt.` });
            // Auswahllisten dieser Sitzung sofort nachziehen (Nr. 148).
            void aktualisiereGesperrteKonten();
            await ladeSperren();
        } finally { setSperreLaeuft(null); }
    };

    const sperrKnopf = (u: UserProfile) => {
        const gesperrt = istGesperrt(sperrStatus?.get(u.id));
        const eigenes = u.id === currentUser?.id;
        const superGeschuetzt = u.role === 'super_admin' && currentUser?.role !== 'super_admin';
        const ohneKonto = !!sperrStatus && !sperrStatus.has(u.id);
        const aus = !sperrStatus || eigenes || superGeschuetzt || ohneKonto || sperreLaeuft === u.id;
        const titel = !sperrStatus ? (sperreHinweis || 'Sperrstatus wird geladen …')
            : eigenes ? 'Das eigene Konto lässt sich nicht sperren.'
            : superGeschuetzt ? 'Einen Super-Admin kann nur ein Super-Admin sperren.'
            : ohneKonto ? 'Zu diesem Profil gibt es kein Anmeldekonto.'
            : gesperrt ? 'Entsperren — die Person kann sich wieder anmelden.'
            : 'Sperren — Anmeldung abweisen, Sitzungen beenden; Profil und Nachweise bleiben.';
        return (
            <button
                onClick={() => { void handleSperre(u, !gesperrt); }}
                disabled={aus}
                title={titel}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed ${gesperrt ? 'text-emerald-700 hover:bg-emerald-50' : 'text-slate-500 hover:text-red-600 hover:bg-red-50'}`}
            >
                {sperreLaeuft === u.id
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin"/>
                    : gesperrt ? <LockOpen className="w-3.5 h-3.5"/> : <Ban className="w-3.5 h-3.5"/>}
                {gesperrt ? 'Entsperren' : 'Sperren'}
            </button>
        );
    };

    const handleDeleteInvite = async (email: string) => {
        if (!confirm(`Einladung für ${email} widerrufen?`)) return;
        const { error } = await supabase.from('user_invites').delete().eq('email', email);
        if (error) alert("Fehler beim Löschen: " + error.message); else onRefresh();
    };

    const handleInviteUser = async () => {
        if (!newUserData.email) return;
        setInviteLoading(true); setStatusFeedback(null);
        let emailSent = false;
        let emailError = '';

        // Einladung über den bestehenden SMTP-Weg (/api/send-email). Die alte
        // Supabase-Function "invite-user" setzte Resend voraus (nicht
        // konfiguriert) und schlug still fehl — Invite stand "ausstehend",
        // ohne dass je eine Mail rausging.
        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session?.access_token) throw new Error('Nicht angemeldet.');
            const appUrl = window.location.origin;
            const inviteHtml = `
                <div style="font-family: sans-serif; color: #333; max-width: 600px; margin: 0 auto;">
                    <h2 style="color: #2563eb;">Willkommen${newUserData.first_name ? `, ${newUserData.first_name}` : ''}!</h2>
                    <p>Sie wurden eingeladen, der Organisation <strong>${newUserData.company || currentUser?.company_name || ''}</strong> auf der SiGeKo Smart Report Plattform beizutreten.</p>
                    <p>Ihre zugewiesene Rolle: <strong>${ROLLEN_EINLADUNG[newUserData.role] || newUserData.role}</strong></p>
                    <br>
                    <p>Bitte registrieren Sie sich unter folgendem Link mit genau dieser E-Mail-Adresse (${newUserData.email}): auf der Anmeldeseite „Eingeladen? Jetzt registrieren“ wählen, Passwort vergeben und danach den Bestätigungslink in der zweiten E-Mail öffnen.</p>
                    <a href="${appUrl}" style="background-color: #2563eb; color: white; padding: 10px 20px; text-decoration: none; border-radius: 5px; display: inline-block;">Jetzt registrieren</a>
                    <br><br>
                    <hr style="border: 0; border-top: 1px solid #eee;">
                    <p style="font-size: 12px; color: #888;">Wenn Sie diese Einladung nicht erwartet haben, können Sie diese E-Mail ignorieren.</p>
                </div>`;
            const response = await fetch('/api/send-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
                body: JSON.stringify({ recipients: [newUserData.email], subject: 'Einladung zu SiGeKo Smart Report', html: inviteHtml })
            });
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error((err as any).error || `HTTP ${response.status}`);
            }
            emailSent = true;
        } catch (e: any) { emailError = e?.message || String(e); console.warn("Email invite failed:", e); }
        
        try {
            const inviteCompanyName = newUserData.company || currentUser?.company_name;
            // Mandant über die ID mitgeben — der Eingeladene tippt seinen
            // Firmennamen bei der Registrierung frei ein (companies.sql). Lädt
            // ein super_admin ausdrücklich in eine andere Firma ein, bleibt die
            // ID leer; der Trigger löst dann über den Namen auf.
            const inviteCompanyId = sameCompany({ company_name: inviteCompanyName }, currentUser)
                ? currentUser?.company_id ?? null
                : null;
            const { error: dbError } = await supabase.from('user_invites').insert([{
                email: newUserData.email,
                first_name: newUserData.first_name,
                last_name: newUserData.last_name,
                role: newUserData.role,
                company_name: inviteCompanyName,
                // Nur mitschicken, wenn wirklich gesetzt: gegen eine Datenbank
                // ohne companies.sql wäre die Spalte unbekannt und das Insert
                // schlüge fehl ("column does not exist"). Ohne Migration ist
                // currentUser.company_id leer, der Schlüssel entfällt.
                ...(inviteCompanyId ? { company_id: inviteCompanyId } : {}),
                invited_by: currentUser?.id
            }]);
            
            if (dbError) throw dbError;
            
            setInviteModal(false);
            if (emailSent) { setStatusFeedback({ type: 'success', message: `Einladung an ${newUserData.email} gesendet.` }); } else { setStatusFeedback({ type: 'warning', message: `Einladung gespeichert, aber E-Mail-Versand fehlgeschlagen: ${emailError || 'unbekannter Fehler'}` }); }
            setNewUserData({ email: '', first_name: '', last_name: '', role: 'user', company: currentUser?.company_name || '' });
            onRefresh();
        } catch (e: any) { setStatusFeedback({ type: 'error', message: "Datenbank-Fehler: " + e.message }); } finally { setInviteLoading(false); }
    };

    return (
        <div className="space-y-8">
            {/* Active Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-4 border-b border-slate-100 bg-slate-50/50 flex justify-between">
                    <div className="relative w-full max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input type="text" placeholder="Benutzer suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"/>
                    </div>
                    <Button variant="primary" onClick={() => setInviteModal(true)} icon={<UserPlus className="w-4 h-4"/>}>Einladen</Button>
                </div>
                
                {statusFeedback && (
                     <div className={`m-4 p-3 rounded-lg text-sm ${statusFeedback.type === 'success' ? 'bg-green-50 text-green-700' : statusFeedback.type === 'warning' ? 'bg-amber-50 text-amber-800' : 'bg-red-50 text-red-700'}`}>
                         {statusFeedback.message}
                     </div>
                )}

                {sperreHinweis && (
                    <div className="m-4 p-3 rounded-lg text-sm bg-amber-50 text-amber-800">
                        {sperreHinweis}
                    </div>
                )}

                {pendingCount > 0 && (
                    <div className="m-4 p-3 rounded-lg text-sm bg-amber-50 text-amber-800 flex items-center gap-2">
                        <Clock className="w-4 h-4 shrink-0"/>
                        {pendingCount === 1 ? '1 Nutzer wartet' : `${pendingCount} Nutzer warten`} auf Freischaltung — Knopf „Freischalten" in der Spalte Status.
                    </div>
                )}

                {/* Eigener Bildlauf: mit der Spalte „Rollen" wird die Tabelle breiter als schmale Fenster. */}
                <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs border-b border-slate-200">
                        <tr><th className="p-4">Name / E-Mail</th><th className="p-4">Firma</th><th className="p-4" title="Systemrolle: Anmeldung und Grundzugang. Admins haben immer alle Rechte; was ein Mitarbeiter darf, kommt aus seinen Rollen.">Systemrolle</th><th className="p-4" title={rollenHinweis || 'Fachliche Rollen der Person — die Rechte addieren sich (Matrix im Reiter „Rollen & Rechte").'}>Rollen</th><th className="p-4">Status</th><th className="p-4">Ersteinweisung</th><th className="p-4" title={sifaHinweis || 'Häkchen „SiFa": darf Unterweisungs-Vorlagen anlegen und bearbeiten (Menü „Unterweisungen").'}>SiFa</th><th className="p-4 text-right">Aktionen</th></tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {filteredUsers.map((u) => (
                            <tr key={u.id} className="hover:bg-slate-50">
                                <td className="p-4"><div className="font-bold text-slate-800">{u.first_name} {u.last_name}</div><div className="text-xs text-slate-500">{u.email}</div></td>
                                <td className="p-4 text-slate-600">{u.company_name}</td>
                                <td className="p-4"><select value={u.role} onChange={(e) => updateUserRole(u.id, e.target.value)} className="bg-transparent border-b border-slate-200 focus:border-indigo-500 outline-none text-xs font-medium pb-1" disabled={u.role === 'super_admin' && currentUser?.role !== 'super_admin'}>{Object.entries(SYSTEMROLLEN).filter(([wert]) => wert !== 'super_admin' || currentUser?.role === 'super_admin' || u.role === 'super_admin').map(([wert, label]) => <option key={wert} value={wert}>{label}</option>)}</select></td>
                                <td className="p-4">{rollenZelle(u)}</td>
                                <td className="p-4">
                                    {istGesperrt(sperrStatus?.get(u.id)) ? (
                                        <span className="px-2 py-1 rounded text-xs font-bold bg-red-100 text-red-700" title="Anmeldung gesperrt (Benutzer sperren, Migration Nr. 138)">Gesperrt</span>
                                    ) : u.is_approved ? (
                                        <span className="px-2 py-1 rounded text-xs font-bold bg-green-100 text-green-700">Aktiv</span>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            <span className="px-2 py-1 rounded text-xs font-bold bg-yellow-100 text-yellow-700">Wartet</span>
                                            <button
                                                onClick={() => approveUser(u)}
                                                disabled={approvalLaeuft === u.id}
                                                title="Zugang freischalten"
                                                className="px-2 py-1 rounded text-xs font-bold bg-green-600 text-white hover:bg-green-700 cursor-pointer disabled:opacity-50 flex items-center gap-1"
                                            >
                                                {approvalLaeuft === u.id ? <Loader2 className="w-3 h-3 animate-spin"/> : <UserCheck className="w-3 h-3"/>} Freischalten
                                            </button>
                                        </div>
                                    )}
                                </td>
                                {/*
                                  * Ersteinweisung (Willkommens-Tour, Migration Nr. 102).
                                  * Drei Zustände, und der dritte ist der wichtige:
                                  *   Zeitstempel → erledigt · null → offen ·
                                  *   undefined   → die Spalte gibt es in dieser Datenbank
                                  *                 noch nicht. Dann steht hier ein Strich
                                  *                 statt „offen" — sonst sähe es aus, als
                                  *                 hätte kein einziger Nutzer je eine Tour
                                  *                 gesehen, und der Rücksetz-Knopf liefe
                                  *                 gegen 42703.
                                  */}
                                <td className="p-4">
                                    {u.ersteinweisung_am === undefined ? (
                                        <span className="text-xs text-slate-400" title="Spalte ersteinweisung_am fehlt — Migration Nr. 102 offen">—</span>
                                    ) : (
                                        <div className="flex items-center gap-2">
                                            {u.ersteinweisung_am ? (
                                                <span className="px-2 py-1 rounded text-xs font-bold bg-green-100 text-green-700 flex items-center gap-1" title={new Date(u.ersteinweisung_am).toLocaleString('de-DE')}>
                                                    <GraduationCap className="w-3 h-3"/> {new Date(u.ersteinweisung_am).toLocaleDateString('de-DE')}
                                                </span>
                                            ) : (
                                                <span className="px-2 py-1 rounded text-xs font-bold bg-slate-100 text-slate-600">offen</span>
                                            )}
                                            {u.ersteinweisung_am && (
                                                <button
                                                    onClick={() => resetErsteinweisung(u)}
                                                    disabled={resetLaeuft === u.id}
                                                    title="Ersteinweisung zurücksetzen — die Tour läuft beim nächsten Anmelden erneut"
                                                    className="text-slate-400 hover:text-indigo-600 disabled:opacity-50 p-1"
                                                >
                                                    {resetLaeuft === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RotateCcw className="w-3.5 h-3.5"/>}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td className="p-4">{sifaHaekchen(u)}</td>
                                <td className="p-4 text-right">{sperrKnopf(u)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
            </div>

            {/* Pending Invites Table */}
            {filteredInvites.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-4 border-b border-slate-100 bg-amber-50/50">
                        <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                            <Mail className="w-4 h-4 text-amber-600"/> Offene Einladungen
                        </h3>
                    </div>
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs border-b border-slate-200">
                            <tr><th className="p-4">E-Mail</th><th className="p-4">Name (Geplant)</th><th className="p-4">Firma</th><th className="p-4">Rolle</th><th className="p-4">Eingeladen am</th><th className="p-4 text-right">Aktion</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {filteredInvites.map((invite) => (
                                <tr key={invite.email} className="hover:bg-slate-50">
                                    <td className="p-4 text-slate-700 font-medium">
                                        {invite.email}
                                        {registrierteEmails.has(invite.email.trim().toLowerCase()) && (
                                            <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-500" title="Zu dieser Adresse gibt es schon ein Konto. Die Einladung erlaubt eine erneute Registrierung — widerrufen, wenn sie nicht gebraucht wird.">bereits registriert</span>
                                        )}
                                    </td>
                                    <td className="p-4 text-slate-600">{invite.first_name} {invite.last_name}</td>
                                    <td className="p-4 text-slate-600">{invite.company_name}</td>
                                    <td className="p-4 text-xs font-mono uppercase">{invite.role}</td>
                                    <td className="p-4 text-xs text-slate-500 flex items-center gap-1">
                                        <Clock className="w-3 h-3"/> {new Date(invite.created_at).toLocaleDateString()}
                                    </td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => handleDeleteInvite(invite.email)} className="text-slate-400 hover:text-red-600 p-2" title="Einladung widerrufen">
                                            <X className="w-4 h-4"/>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Invite Modal */}
            {inviteModal && (
                <div className="fixed inset-0 z-[100] bg-black/60 animate-fade-in backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
                         <div className="flex justify-between items-center mb-6">
                             <h3 className="font-bold text-lg text-slate-800">Nutzer einladen</h3>
                             <button onClick={() => setInviteModal(false)}><X className="w-5 h-5 text-slate-400 hover:text-red-500" /></button>
                         </div>
                         <div className="space-y-4">
                             <div>
                                 <label className="text-xs font-bold text-slate-500 uppercase block mb-1">E-Mail Adresse</label>
                                 <input type="email" value={newUserData.email} onChange={e => setNewUserData({...newUserData, email: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="max@firma.de" />
                             </div>
                             <div className="grid grid-cols-2 gap-4">
                                 <div><label className="text-xs font-bold text-slate-500 uppercase block mb-1">Vorname</label><input type="text" value={newUserData.first_name} onChange={e => setNewUserData({...newUserData, first_name: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2 text-sm" placeholder="Max" /></div>
                                 <div><label className="text-xs font-bold text-slate-500 uppercase block mb-1">Nachname</label><input type="text" value={newUserData.last_name} onChange={e => setNewUserData({...newUserData, last_name: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2 text-sm" placeholder="Mustermann" /></div>
                             </div>
                             <div>
                                 <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Systemrolle</label>
                                 {/* Seit Nr. 124 ist die Einladung der einzige Weg ins System und
                                     ihre Rolle wird beim Registrieren übernommen — deshalb wählbar,
                                     auch „Freelancer" (vorher registrierten die sich selbst). */}
                                 <select value={newUserData.role} onChange={e => setNewUserData({...newUserData, role: e.target.value})} className="w-full border border-slate-300 rounded-lg p-2 text-sm bg-white">
                                     {Object.entries(ROLLEN_EINLADUNG).map(([wert, label]) => <option key={wert} value={wert}>{label}</option>)}
                                 </select>
                                 <p className="mt-1 text-xs text-slate-500">Fachliche Rollen (SiGeKo, Buchhaltung …) nach der Registrierung in der Spalte „Rollen" vergeben.</p>
                             </div>
                         </div>
                         <div className="mt-6 flex justify-end">
                             <Button variant="primary" onClick={handleInviteUser} disabled={inviteLoading || !newUserData.email} icon={inviteLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}>Einladung senden</Button>
                         </div>
                    </div>
                </div>
            )}
        </div>
    );
};
